/**
 * calcHS — Horizontal Slider cut list calculations
 *
 * All formulas verified line-by-line against HS DNA spreadsheet.
 * Excel cell references noted inline for auditing.
 *
 * WindowRow.jsx stores glass fields with prefix "Keeper":
 *   win.KeeperGlassPkg   win.KeeperGlassOpts
 *   win.KeeperGridType   win.KeeperGridPat   win.KeeperGridV   win.KeeperGridH
 *
 * HS has ONE glass pane: "Slider Glass" — uses Keeper prefix fields.
 *
 * Formula key (DNA sheet):
 *   B21  = indivW  (overall width ÷ numLites)
 *   C21  = indivH  (overall height — HS is single-height)
 *   B19  = unitQty (qty × numLites, i.e. per-lite qty)
 *   C50  = sashH_ref = C21 - 4.75
 *   D50  = sashW_ref = B21/2 - 1.375
 *   Glass W = D50 - 2 = sashW_ref - 2
 *   Glass H = C50 - 2 = sashH_ref - 2
 */
export function calcHS(win) {
  const {
    // ── Dimensions ──────────────────────────────────────────────────────────
    width: overallW,
    height,
    // ── Identity ────────────────────────────────────────────────────────────
    config    = 'Single',
    color     = 'White',
    finType   = 'Without Fin',
    qty       = 1,
    label     = '',
    // ── Screen ──────────────────────────────────────────────────────────────
    screenType = 'None',
    screenSize = 'Full Scrn',
    meshType   = 'STD Mesh',
    // ── Gas ─────────────────────────────────────────────────────────────────
    gasFill    = 'Argon',
    // ── Glass — HS uses Keeper prefix (single pane) ─────────────────────────
    KeeperGlassPkg  = 'DLA',
    KeeperGlassOpts = 'None',
    KeeperGridType  = 'None',
    KeeperGridPat   = 'None',
    KeeperGridV     = '0',
    KeeperGridH     = '0',
  } = win;

  // ── Color helpers ─────────────────────────────────────────────────────────
  const colorLower  = (color || '').toLowerCase();
  const isDark      = colorLower.includes('woodgrain');
  const withFin     = finType === 'With Fin' || finType === 'With Fin & J';

  // Glazbead color logic from DNA:
  // IF(B10="dk woodgrain/white", "White", IF(B10="dk woodgrain/clay", "Clay", B10))
  const glazbeadColor = colorLower.includes('white') ? 'White'
                      : colorLower.includes('clay')  ? 'Clay'
                      : color;

  // ── Lite multiplier ───────────────────────────────────────────────────────
  // liteMap matches DNA: single=1, twin=2, triple=3, quad=4, quin=5, q/h/q=2 (treated as twin)
  const liteMap = {
    Single:   1,
    '2-Lite': 2,
    '3-Lite': 3,
    '4-Lite': 4,
    '5-Lite': 5,
    'Q/H/Q':  2,
    // Legacy names still accepted
    Twin: 2, Triple: 3, Quad: 4, Quin: 5,
  };
  const numLites = liteMap[config] || 1;
  const indivW   = overallW / numLites;   // B21 per lite
  const indivH   = height;                // C21
  const unitQty  = qty * numLites;        // B19 = unit qty per lite × numLites

  // ── Sash reference sizes ──────────────────────────────────────────────────
  // From DNA SASH SIZE row:
  //   C50 = sashH_ref = C21 - 4.75    (indivH - 4.75)
  //   D50 = sashW_ref = B21/2 - 1.375 (indivW/2 - 1.375)
  const sashH_ref = indivH - 4.75;           // C50
  const sashW_ref = indivW / 2 - 1.375;      // D50

  // ── Glass ─────────────────────────────────────────────────────────────────
  // Glass row DNA:
  //   Qty    = B19 * 2                 (unitQty × 2)
  //   Width  = D50 - 2 = sashW_ref-2  (indivW/2 - 3.375)
  //   Height = C50 - 2 = sashH_ref-2  (indivH - 6.75)
  const glassW = sashW_ref - 2;              // indivW/2 - 3.375
  const glassH = sashH_ref - 2;              // indivH   - 6.75

  // ── Grid helper ───────────────────────────────────────────────────────────
  // Grid Length H = glassW - 1  (DNA: =L10-1 where L10=glassW)
  // Grid Length V = glassH - 1  (DNA: =L11-1 where L11=glassH)
  const gridV    = parseInt(KeeperGridV) || 0;
  const gridH    = parseInt(KeeperGridH) || 0;
  const hasGrids = !!KeeperGridType
                && KeeperGridType !== 'None'
                && KeeperGridType !== ''
                && (gridV > 0 || gridH > 0);

  const gridLenH  = hasGrids ? glassW - 1 : null;   // DNA: =L10-1
  const gridLenV  = hasGrids ? glassH - 1 : null;   // DNA: =L11-1
  const horizQty  = hasGrids ? gridH * unitQty : 0;  // DNA: =L17*L9 (H bars × unitQty)
  const vertQty   = hasGrids ? gridV * unitQty : 0;  // DNA: =M17*L9 (V bars × unitQty)
  // Grid Color: IF(gridType="Flat"|"Contour", color, "")
  // Grid color matches INSIDE-visible frame color.
  // Frame naming: "Inside/Outside" (e.g. "Dk Woodgrain/White" = dark inside, white outside).
  // Inside grid face matches inside frame color; outside grid face is always White.
  // We report the INSIDE color so Tracy Corners knows what to build.
  const insideGridColor = color.includes('/')
    ? color.split('/')[0].trim()
    : color;
  const gridColor = hasGrids ? insideGridColor : '';

  // ── Frame ─────────────────────────────────────────────────────────────────
  // Slider Frame Widths:  qty=B19*2, length=B21+0.25=indivW+0.25, part=5153-color
  // Slider Frame Heights: qty=B19*2, length=C21+0.25=indivH+0.25, part=5153-color
  const frameW    = indivW + 0.25;               // B21+0.25
  const frameH    = indivH + 0.25;               // C21+0.25
  const framePart = `5153-${color}`;

  // ── Sash ──────────────────────────────────────────────────────────────────
  // Sash Widths:      qty=B19*4, length=D50+0.25=sashW_ref+0.25, part=5540-color
  // Keeper Rail:      qty=B19*1, length=C50+0.25=sashH_ref+0.25, part=5543-color
  // Keeper Lift Rail: qty=B19*1, length=C50+0.25,                part=5541-color
  // Lock Lift Rail:   qty=B19*1, length=C50+0.25,                part=5541-color
  // Lock Rail:        qty=B19*1, length=C50+0.25,                part=5542-color
  const sashWidthCut = sashW_ref + 0.25;         // D50+0.25
  const railLen      = sashH_ref + 0.25;         // C50+0.25

  // ── Reinforcements ────────────────────────────────────────────────────────
  // 665 Lock Reinf + 665 Keeper Reinf: each qty=B19*1, length=C21-6.6875.
  // Identical cut, so combined into one "(x2)" line at B19*2 below.
  const reinfLen = indivH - 6.6875;             // C21-6.6875

  // ── Glazbead ─────────────────────────────────────────────────────────────
  // (x2) Glazbead Widths:  qty=B19*2, length=D50-1.6875=sashW_ref-1.6875, part=5457-glazbeadColor
  // (x2) Glazbead Heights: qty=B19*2, length=C50-2.9375=sashH_ref-2.9375, part=5457-glazbeadColor
  // DNA part formula: IF(color="dk woodgrain/white","5457-White",IF(..."5457-Clay","5457-color"))
  const glazbeadW    = sashW_ref - 1.6875 - 0.0625;  // -1/16 adjustment
  const glazbeadH    = sashH_ref - 2.9375 + 0.125;  // +1/8 adjustment
  const glazbeadPart = `5457-${glazbeadColor}`;

  // ── Misc parts ────────────────────────────────────────────────────────────
  // (x2) Slider Track Filler: qty=B19*2 (two per unit — the "(x2)" in the label),
  // length=B21-3.5=indivW-3.5, part=5660-color
  const trackFillerW = indivW - 3.5;

  // Starter: qty = Without Fin → B19*1, With Fin → 0; length = B12 = overallW (full OA width)
  // DNA: =IF(A14="without fin",(B19*1),IF(A14="with fin",(B19*0)))
  const starterQty  = withFin ? 0 : unitQty;
  const starterPart = isDark
    ? `8351-${glazbeadColor}`
    : `8351-${color}`;

  // (x2) Snap Mull: qty per config × qty, length=C21-0.125=indivH-0.125, part=8309-color
  // DNA qty: single=0, twin=qty*1, triple=qty*2, quad=qty*3, quin=qty*4, q/h/q=qty*2
  const snapMullQtyMap = { Single:0,'2-Lite':1,'3-Lite':2,'4-Lite':3,'5-Lite':4,'Q/H/Q':2, Twin:1,Triple:2,Quad:3,Quin:4 };
  const snapMullQty    = (snapMullQtyMap[config] || 0) * qty;
  const snapMullLen    = indivH - 0.125;         // C21-0.125

  // ── Balance notch ─────────────────────────────────────────────────────────
  // DNA: =IF(C21<=31.9375,"4'' Notch","7'' Notch")  where C21 = indivH
  const notch = indivH <= 31.9375 ? "4'' Notch" : "7'' Notch";

  // ── Flex Screen ───────────────────────────────────────────────────────────
  // DNA: Full Scrn W = C46-4.5 = indivW-4.5 (C46=indivW in HS FRAME SIZE row)
  //       Half Scrn W = (C46-4.625)/2 = (indivW-4.625)/2
  //       H            = D46-4.4375   = indivH-4.4375
  const isFlexScrn    = screenType === 'Flex Scrn';
  const isStdScrn     = screenType === 'Std Scrn';
  const flexScreenW   = screenSize === 'Half Scrn'
    ? (indivW - 4.625) / 2
    : indivW - 4.5;
  const flexScreenH   = indivH - 4.4375;

  // ── Std Screen ────────────────────────────────────────────────────────────
  // Screen Heights:         qty=B19*2,                   length=C21-6.375=indivH-6.375
  // Screen Widths w/ Pull:  qty=IF(B21<50,B19*1,B19*2), length=B21-6.75=indivW-6.75
  // Screen Widths w/o Pull: qty=IF(B21<50,B19*1,0),     length=B21-6.75
  const stdScreenH        = indivH - 6.375;
  const stdScreenW        = indivW - 6.75;
  const stdScreenHQty     = isStdScrn ? unitQty * 2 : 0;
  const stdScreenPullQty  = isStdScrn ? (indivW < 50 ? unitQty : unitQty * 2) : 0;
  const stdScreenNoPullQty= isStdScrn ? (indivW < 50 ? unitQty : 0) : 0;
  const screenPart        = isDark
    ? `SCRNPLN-${glazbeadColor}`
    : `SCRNPLN-${color}`;
  const screenPullPart    = isDark
    ? `SCRNPULL-${glazbeadColor}`
    : `SCRNPULL-${color}`;

  const flexScreen = isFlexScrn ? {
    qty:        unitQty,
    width:      flexScreenW,
    height:     flexScreenH,
    meshType:   meshType || 'STD Mesh',
    screenType,
    label,
  } : null;

  // ── Return ────────────────────────────────────────────────────────────────
  return {
    label,
    type:       'HS',
    color,
    overallW,
    width:      overallW,
    height,
    config,
    qty,
    totalQty:   unitQty,
    indivW,
    finType,
    screenType,
    screenSize,

    // ── Glass — single pane "Slider Glass" ──────────────────────────────────
    // qty = unitQty * 2 (two panes of glass per lite — left and right sash)
    glass: [
      {
        label:       'Slider Glass',
        qty:         unitQty * 2,
        width:       glassW,
        height:      glassH,
        glassType:   KeeperGlassPkg,
        glassConfig: KeeperGlassOpts !== 'None' ? KeeperGlassOpts : '',
        gasFill,
        // Grid fields
        grids:       hasGrids,
        gridType:    hasGrids ? KeeperGridType   : '',
        gridPattern: hasGrids ? (KeeperGridPat === 'None' ? '' : KeeperGridPat) : '',
        gridQtyV:    hasGrids ? gridV : 0,
        gridQtyH:    hasGrids ? gridH : 0,
        gridColor:   hasGrids ? gridColor : '',
        gridLenH,
        gridLenV,
        horizQty,
        vertQty,
      },
    ],

    // ── Frame ───────────────────────────────────────────────────────────────
    // qty = unitQty*2 for both widths and heights (one per side per lite)
    frame: [
      { label: 'Slider Frame Widths',  qty: unitQty * 2, length: frameW, part: framePart },
      { label: 'Slider Frame Heights', qty: unitQty * 2, length: frameH, part: framePart },
    ],

    // ── Sash ────────────────────────────────────────────────────────────────
    sash: [
      { label: 'Sash Widths',      qty: unitQty * 4, length: sashWidthCut, part: `5540-${color}` },
      { label: 'Keeper Rail',      qty: unitQty,     length: railLen,      part: `5543-${color}` },
      { label: 'Keeper Lift Rail', qty: unitQty,     length: railLen,      part: `5541-${color}` },
      { label: 'Lock Lift Rail',   qty: unitQty,     length: railLen,      part: `5541-${color}` },
      { label: 'Lock Rail',        qty: unitQty,     length: railLen,      part: `5542-${color}` },
    ],

    // ── Reinforcements ──────────────────────────────────────────────────────
    // Lock and Keeper reinf are identical here (same length, same part), so
    // they print as one line at 2× qty instead of two duplicate rows.
    reinforcement: [
      { label: '(x2) 665 Lock/Keeper Reinf', qty: unitQty * 2, length: reinfLen, part: '665' },
    ],

    // ── Glazbead ────────────────────────────────────────────────────────────
    glazbead: [
      { label: '(x2) Glazbead Widths',  qty: unitQty * 2, length: glazbeadW, part: glazbeadPart },
      { label: '(x2) Glazbead Heights', qty: unitQty * 2, length: glazbeadH, part: glazbeadPart },
    ],

    // ── Misc ────────────────────────────────────────────────────────────────
    misc: [
      { label: '(x2) Slider Track Filler', qty: unitQty * 2, length: trackFillerW, part: `5660-${color}` },
      ...(starterQty > 0 ? [
        { label: 'Starter', qty: starterQty, length: overallW, part: starterPart },
      ] : []),
      ...(snapMullQty > 0 ? [
        { label: '(x2) Snap Mull', qty: snapMullQty, length: snapMullLen, part: `8309-${color}` },
      ] : []),
    ],

    // ── Balance ─────────────────────────────────────────────────────────────
    balance: {
      notch,
      qty: unitQty * 2,
    },

    // ── Screens ─────────────────────────────────────────────────────────────
    flexScreen,
    stdScreen: isStdScrn ? {
      height:      stdScreenH,
      width:       stdScreenW,
      heightQty:   stdScreenHQty,
      withPullQty: stdScreenPullQty,
      noPullQty:   stdScreenNoPullQty,
      screenColor: color,
      label,
    } : null,

    // ── Summary / reference sizes ────────────────────────────────────────────
    // Matches DNA FRAME SIZE and SASH SIZE rows
    summary: {
      sashWidth:  sashW_ref,    // D50 = indivW/2 - 1.375
      sashHeight: sashH_ref,    // C50 = indivH   - 4.75
      glassW,
      glassH,
    },
  };
}